<script setup lang="ts">
import { onMounted, ref } from 'vue'
import type { ComponentPublicInstance } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import Skeleton from '@/components/Skeleton.vue'
import { userModule } from '@/store/user/module'

const {
    ModalContainer,
    Button: PocketButton,
    Input: PocketInput,
    Link: PocketLink,
    Icon,
} = UIKit

const route = useRoute()
const router = useRouter()

const email = ref('')
const password = ref('')
const signInWithPassword = ref(false)
const errorMessage = ref('')
const isLoading = ref(false)

const passwordInput = ref<ComponentPublicInstance | null>(null)

onMounted(() => {
    if (typeof route.query.error === 'string') {
        errorMessage.value = route.query.error
    }
})

const submitSignIn = async () => {
    isLoading.value = true
    errorMessage.value = ''

    if (
        signInWithPassword.value
        && email.value
        && password.value
    ) {
        try {
            const user = await userModule.actions.signIn({
                username: email.value,
                password: password.value,
            })
            if (!user.currentOrganization) {
                router.push({ name: 'register' })
            } else if (route.query.redirect && typeof route.query.redirect === 'string') {
                router.push({ path: route.query.redirect })
            } else {
                router.push({ name: 'stats' })
            }
        } catch (err) {
            if (err instanceof Error && err.message === 'Invalid username/password.') {
                errorMessage.value = 'Incorrect email or password'
            } else if (err instanceof Error) {
                errorMessage.value = err.message
            }
        }
    } else if (
        !signInWithPassword.value
        && email.value
    ) {
        try {
            await userModule.actions.sendMagicEmail({ email: email.value })
            router.push({
                name: 'email-auth',
                query: {
                    email: email.value.toLowerCase(),
                    redirect: route.query.redirect,
                },
            })
        } catch (err) {
            errorMessage.value = 'We weren\'t able to send the link'
        }
    } else if (!email.value) {
        errorMessage.value = 'Email is required'
    } else if (signInWithPassword.value && !password.value) {
        errorMessage.value = 'Password is required'
    }
    isLoading.value = false
}

const togglePasswordSignIn = () => {
    errorMessage.value = ''
    signInWithPassword.value = !signInWithPassword.value

    // If the user is toggling signing in via password and has already entered an email, focus the password field
    if (signInWithPassword.value && email.value) {
        setTimeout(() => {
            const passwordInputEl = passwordInput.value?.$el.querySelector('input') as HTMLInputElement | null
            passwordInputEl?.focus()
        }, 0)
    }
}
</script>

<template>
    <Skeleton class="sign-in">
        <Portal to="modal">
            <ModalContainer key="signInModal">
                <div
                    class="sign-in__form"
                >
                    <img
                        class="sign-in__blobs-left"
                        src="@/assets/images/sign-in-blobs-left.svg"
                    >
                    <img
                        class="sign-in__blob-right"
                        src="@/assets/images/sign-in-blob-right.svg"
                    >
                    <div class="sign-in__text-big">
                        Sign In
                    </div>
                    <div v-if="!signInWithPassword" class="sign-in__text-small">
                        We'll email you a link to sign in password-free
                    </div>
                    <PocketInput
                        v-model="email"
                        class="sign-in__email"
                        field-type="email"
                        label="Email"
                        @keydown.enter="!signInWithPassword && submitSignIn()"
                    />
                    <PocketInput
                        v-if="signInWithPassword"
                        ref="passwordInput"
                        v-model="password"
                        class="sign-in__password"
                        field-type="password"
                        label="Password"
                        @keydown.enter="signInWithPassword && submitSignIn()"
                    />
                    <div v-if="errorMessage" class="sign-in__error-message">
                        <Icon class="sign-in__error-message-icon" type="warning" />
                        <span>{{ errorMessage }}</span>
                    </div>
                    <div v-if="signInWithPassword" class="sign-in__password-options">
                        <PocketButton
                            class="sign-in__use-magic-link-btn"
                            type="tertiary"
                            :disabled="isLoading"
                            @click.prevent="togglePasswordSignIn"
                        >
                            Sign in with a link
                        </PocketButton>
                        <PocketButton
                            class="sign-in__password-submit"
                            :disabled="isLoading"
                            @click="submitSignIn"
                        >
                            <span>Sign In</span> <Icon type="arrow" class="sign-in__password-submit-arrow" />
                        </PocketButton>
                    </div>
                    <div v-else class="sign-in__magic-email-options">
                        <PocketButton
                            class="sign-in__use-password-btn"
                            type="tertiary"
                            :disabled="isLoading"
                            @click.prevent="togglePasswordSignIn"
                        >
                            Sign in with password
                        </PocketButton>
                        <PocketButton
                            class="sign-in__magic-submit"
                            :disabled="isLoading"
                            @click="submitSignIn"
                        >
                            Send Link <Icon type="arrow" class="sign-in__magic-submit-arrow" />
                        </PocketButton>
                    </div>
                    <div class="sign-in__footer">
                        New?
                        <PocketLink
                            class="sign-in__create-account-link"
                            :href="router.resolve({ name: 'register' }).href"
                            @click.prevent="router.push({ name: 'register' })"
                        >
                            Create an account
                        </PocketLink>
                        <span class="sign-in__footer-middot">•</span>
                        <PocketLink
                            class="sign-in__help-link"
                            href="https://help.educators.pocketprep.com"
                            target="_blank"
                        >
                            I need help
                        </PocketLink>
                    </div>
                </div>
            </ModalContainer>
        </Portal>
    </Skeleton>
</template>

<style lang="scss" scoped>


.sign-in {
    &__form {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 660px;
        height: 500px;
        background-color: $floral-white;
    }

    &__blobs-left {
        position: absolute;
        top: 0;
        left: 0;
    }

    &__blob-right {
        position: absolute;
        bottom: 45px;
        right: 0;
    }

    &__text-big {
        font-size: 36px;
        line-height: 46px;
        margin-bottom: 17px;
        font-weight: 700;
    }

    &__text-small {
        font-size: 16px;
        line-height: 21.86px;
        margin-bottom: 32px;
        color: $slate-03;
    }

    &__email,
    &__password {
        padding: 10px;
        width: 340px;
    }

    &__error-message {
        display: flex;
        align-items: center;
        color: #c238f3;
        font-size: 14px;
        line-height: 19px;
        margin-bottom: 18px;
    }

    &__error-message-icon {
        margin-right: 10px;
    }

    &__password-options,
    &__magic-email-options {
        margin-top: 14px;
        display: flex;
        justify-content: center;
        width: 320px;
    }

    &__use-password-btn,
    &__use-magic-link-btn {
        font-size: 14px !important;
        margin-right: 15px;
    }

    &__password-submit,
    &__magic-submit {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: 0 16px;
    }

    &__password-submit-arrow,
    &__magic-submit-arrow {
        margin-left: 6px;
    }

    &__footer {
        font-size: 14px;
        font-weight: 400;
        position: absolute;
        bottom: 0;
        height: 60px;
        display: flex;
        align-items: center;
    }

    &__create-account-link,
    &__help-link {
        color: $brand-blue;
        margin: 0 16px;
        font-size: 14px !important;
    }

    &__create-account-link {
        margin-left: 4px;
    }

    &__footer-middot {
        color: $steel;
    }
}
</style>
